import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  useTheme,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Area,
  AreaChart,
  ComposedChart,
  Legend,
  ReferenceLine
} from 'recharts';
import { TrendingUp, Activity, FlaskConical, Zap } from 'lucide-react';
import { api } from '../../lib/api';
import { safeConsole } from '../../lib/piiProtection';

// Parse markdown AI summary into structured sections
interface AiInsightsParsedProps {
  summary: string;
  theme: any;
}

// Parse vital measurements from Recent Trends section
const parseVitalTrends = (summary: string) => {
  const trendsMatch = summary.match(/Recent Trends[^###]*/);
  if (!trendsMatch) return [];

  const trendsText = trendsMatch[0];
  const lines = trendsText.split('\n').filter(l => l.includes('"'));

  const vitals: any[] = [];
  lines.forEach(line => {
    const match = line.match(/\*\*(.+?)\*\*\s*"(.+?)"\s*([^\s]+)/);
    if (match) {
      const [, name, value, status] = match;
      const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));
      if (!isNaN(numValue)) {
        vitals.push({
          name: name.trim(),
          value: numValue,
          status: status.replace(/[()]/g, '').trim(),
          rawValue: value
        });
      }
    }
  });
  return vitals;
};

const parseRisks = (summary: string) => {
  const risksMatch = summary.match(/Key Risks[^###]*/);
  if (!risksMatch) return [];

  const risksText = risksMatch[0];
  const lines = risksText.split('\n').filter(l => l.includes('-'));

  const risks: any[] = [];
  lines.forEach((line, idx) => {
    const risk = line.replace(/^[-*]\s*/, '').replace(/\*\*/g, '').trim();
    if (risk && risk.length > 0) {
      risks.push({
        id: idx,
        name: risk.split(':')[0].trim(),
        description: risk.split(':').slice(1).join(':').trim(),
        severity: risk.includes('high') || risk.includes('critical') ? 1 :
                  risk.includes('moderate') ? 2 : 3
      });
    }
  });
  return risks.sort((a, b) => a.severity - b.severity);
};

// Parse medications from Active Medications section
const parseMedications = (summary: string) => {
  const medsMatch = summary.match(/Active Medications[^###]*/);
  if (!medsMatch) return { total: 0, categories: [] };

  const medsText = medsMatch[0];
  const meds = medsText.split('\n').filter(l => l.match(/Tab\.|Syrup|Sachet/));

  const categories: Record<string, number> = {};
  meds.forEach(med => {
    const type = med.includes('Tab.') ? 'Tablets' :
                 med.includes('Syrup') ? 'Syrups' : 'Sachet';
    categories[type] = (categories[type] || 0) + 1;
  });

  return {
    total: meds.length,
    categories: Object.entries(categories).map(([name, count]) => ({ name, value: count }))
  };
};

// Aggregate vital parameters from visits into trend data
const aggregateVitalsFromVisits = (visits: any[]): Record<string, TrendPoint[]> => {
  if (!visits || visits.length === 0) return {};

  const vitalsMap: Record<string, TrendPoint[]> = {};

  // Sort visits by date
  const sortedVisits = [...visits].sort((a, b) =>
    new Date(a.visitDate).getTime() - new Date(b.visitDate).getTime()
  );

  sortedVisits.forEach(visit => {
    const date = new Date(visit.visitDate).toISOString().split('T')[0];

    // Blood Pressure - combine systolic and diastolic
    if (visit.systolicBP && visit.diastolicBP) {
      const bpKey = 'Blood Pressure (mmHg)';
      if (!vitalsMap[bpKey]) vitalsMap[bpKey] = [];
      vitalsMap[bpKey].push({
        date,
        value: `${visit.systolicBP}/${visit.diastolicBP}`,
        unit: 'mmHg',
        status: 'recorded'
      });
    }

    // Pulse
    if (visit.pulse !== null && visit.pulse !== undefined) {
      const key = 'Pulse (bpm)';
      if (!vitalsMap[key]) vitalsMap[key] = [];
      vitalsMap[key].push({
        date,
        value: visit.pulse,
        unit: 'bpm',
        status: 'recorded'
      });
    }

    // Temperature
    if (visit.temperature !== null && visit.temperature !== undefined) {
      const key = 'Temperature (°C)';
      if (!vitalsMap[key]) vitalsMap[key] = [];
      vitalsMap[key].push({
        date,
        value: visit.temperature,
        unit: '°C',
        status: 'recorded'
      });
    }

    // SpO2
    if (visit.spO2 !== null && visit.spO2 !== undefined) {
      const key = 'Oxygen Saturation (%)';
      if (!vitalsMap[key]) vitalsMap[key] = [];
      vitalsMap[key].push({
        date,
        value: visit.spO2,
        unit: '%',
        status: 'recorded'
      });
    }

    // Respiratory Rate
    if (visit.respiratoryRate !== null && visit.respiratoryRate !== undefined) {
      const key = 'Respiratory Rate (breaths/min)';
      if (!vitalsMap[key]) vitalsMap[key] = [];
      vitalsMap[key].push({
        date,
        value: visit.respiratoryRate,
        unit: 'breaths/min',
        status: 'recorded'
      });
    }

    // Weight
    if (visit.weight !== null && visit.weight !== undefined) {
      const key = 'Weight (kg)';
      if (!vitalsMap[key]) vitalsMap[key] = [];
      vitalsMap[key].push({
        date,
        value: visit.weight,
        unit: 'kg',
        status: 'recorded'
      });
    }
  });

  return vitalsMap;
};

const getParameterConfig = (paramName: string, theme: any) => {
  const lower = paramName.toLowerCase();

  if (lower.includes('blood pressure') || lower.includes('bp')) {
    return {
      type: 'bp',
      colors: ['#e91e63', '#2196f3'],
      icon: '🩸',
      title: 'Blood Pressure',
      normalRange: { sys: [90, 120], dia: [60, 80] }
    };
  }
  if (lower.includes('glucose') || lower.includes('sugar')) {
    return {
      type: 'glucose',
      color: '#ff9800',
      icon: '🍬',
      title: 'Blood Glucose',
      normalRange: [70, 100]
    };
  }
  if (lower.includes('cholesterol') || lower.includes('ldl') || lower.includes('hdl') || lower.includes('triglyceride')) {
    return {
      type: 'lipid',
      color: '#9c27b0',
      icon: '🧪',
      title: paramName,
      normalRange: [0, 200]
    };
  }
  if (lower.includes('hemoglobin') || lower.includes('hb') || lower.includes('hct')) {
    return {
      type: 'blood',
      color: '#e91e63',
      icon: '🩹',
      title: paramName,
      normalRange: [12, 18]
    };
  }
  if (lower.includes('creatinine') || lower.includes('urea') || lower.includes('bun')) {
    return {
      type: 'kidney',
      color: '#4caf50',
      icon: '🫘',
      title: paramName,
      normalRange: [0.5, 1.2]
    };
  }
  if (lower.includes('thyroid') || lower.includes('tsh')) {
    return {
      type: 'thyroid',
      color: '#00bcd4',
      icon: '🧬',
      title: paramName,
      normalRange: [0.4, 4.0]
    };
  }

  return {
    type: 'default',
    color: theme.palette.secondary.main,
    icon: '📊',
    title: paramName,
    normalRange: null
  };
};

// Render chart based on parameter type
const ParameterChart: React.FC<{ testName: string; data: TrendPoint[]; theme: any }> = ({
  testName,
  data,
  theme
}) => {
  const config = getParameterConfig(testName, theme);
  const unit = data[0]?.unit || '';

  if (config.type === 'bp' && data.length > 0) {
    // Blood Pressure - parse systolic and diastolic
    const bpData = data.map(point => {
      const [systolic, diastolic] = point.value.toString().split('/').map(Number);
      return {
        date: point.date,
        systolic: systolic || 0,
        diastolic: diastolic || 0,
        formattedDate: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      };
    });

    return (
      <Card variant="outlined" sx={{ borderRadius: 2, borderTop: `4px solid ${config.colors[0]}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography sx={{ fontSize: '24px' }}>{config.icon}</Typography>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="primary">
                  {config.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Normal: 90-120 / 60-80 mmHg
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`Latest: ${bpData[bpData.length - 1].systolic}/${bpData[bpData.length - 1].diastolic}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>

          <Box sx={{ height: 280, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={bpData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                <XAxis
                  dataKey="formattedDate"
                  tick={{ fontSize: 10 }}
                />
                <YAxis tick={{ fontSize: 10 }} domain={[40, 160]} />
                <Tooltip
                  contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: theme.shadows[3],
                    backgroundColor: theme.palette.background.paper
                  }}
                  formatter={(value: any) => `${value} mmHg`}
                />
                <Legend />
                <ReferenceLine y={120} stroke="#e91e63" strokeDasharray="5 5" label="Systolic Threshold" />
                <Line
                  type="monotone"
                  dataKey="systolic"
                  stroke={config.colors[0]}
                  strokeWidth={3}
                  dot={{ r: 4, fill: config.colors[0] }}
                  activeDot={{ r: 6 }}
                  name="Systolic"
                />
                <Line
                  type="monotone"
                  dataKey="diastolic"
                  stroke={config.colors[1]}
                  strokeWidth={3}
                  dot={{ r: 4, fill: config.colors[1] }}
                  activeDot={{ r: 6 }}
                  name="Diastolic"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Default chart for other parameters
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderTop: `4px solid ${config.color}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '24px' }}>{config.icon}</Typography>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary">
                {testName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Unit: {unit}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`Latest: ${data[data.length - 1].value} ${unit}`}
            style={{ borderColor: config.color, color: config.color }}
            variant="outlined"
            size="small"
          />
        </Box>

        <Box sx={{ height: 280, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id={`colorArea-${testName}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={config.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={config.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(str) => {
                  const d = new Date(str);
                  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: theme.shadows[3],
                  backgroundColor: theme.palette.background.paper
                }}
                formatter={(value: any) => [`${value} ${unit}`, testName]}
              />
              {config.normalRange && (
                <ReferenceLine
                  y={Array.isArray(config.normalRange) ? config.normalRange[1] : config.normalRange}
                  stroke={config.color}
                  strokeDasharray="5 5"
                  label={`Normal: ${Array.isArray(config.normalRange) ? config.normalRange.join('-') : config.normalRange}`}
                  opacity={0.5}
                />
              )}
              <Area
                type="monotone"
                dataKey="value"
                stroke={config.color}
                fill={`url(#colorArea-${testName})`}
                strokeWidth={3}
                dot={{ r: 4, fill: config.color }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const AiInsightsParsed: React.FC<AiInsightsParsedProps> = ({ summary, theme }) => {
  const sections = summary.split('###').filter(s => s.trim());

  const parseSection = (content: string) => {
    const lines = content.split('\n').filter(l => l.trim());
    if (!lines.length) return null;

    const title = lines[0].replace(/\*\*/g, '').trim();
    const items = lines.slice(1).map(line => {
      const text = line.replace(/^[-*]\s*/, '').replace(/\*\*/g, '').trim();
      return text;
    }).filter(t => t);

    return { title, items };
  };

  // Parse vital data for charts
  const vitals = parseVitalTrends(summary);

  const getRiskColor = (title: string) => {
    const lower = title.toLowerCase();
    if (lower.includes('risk')) return 'error';
    if (lower.includes('status')) return 'info';
    if (lower.includes('trend')) return 'success';
    if (lower.includes('medication')) return 'warning';
    return 'default';
  };

  const getRiskIcon = (title: string) => {
    const lower = title.toLowerCase();
    if (lower.includes('risk')) return '⚠️';
    if (lower.includes('status')) return '📋';
    if (lower.includes('trend')) return '📈';
    if (lower.includes('medication')) return '💊';
    if (lower.includes('brief')) return '📝';
    return '•';
  };

  return (
    <Stack spacing={3}>
      {/* Vital Measurements Chart */}
      {vitals.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Activity size={18} color={theme.palette.primary.main} />
              <Typography variant="subtitle2" fontWeight={700} color="primary">
                Recent Vital Measurements
              </Typography>
            </Box>
            <Box sx={{ height: 300, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={vitals.map(v => ({
                    name: v.name,
                    value: v.value,
                    status: v.status
                  }))}
                  margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, angle: -45, textAnchor: 'end', height: 80 }}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '8px',
                      border: 'none',
                      boxShadow: theme.shadows[3],
                      backgroundColor: theme.palette.background.paper
                    }}
                    formatter={(value: any) => [value.toFixed(2), 'Value']}
                  />
                  <Bar dataKey="value" fill={theme.palette.secondary.main} radius={[8, 8, 0, 0]}>
                    {vitals.map((vital, idx) => (
                      <Cell
                        key={`cell-${idx}`}
                        fill={
                          vital.status.includes('Normal') ? '#4caf50' :
                          vital.status.includes('Elevated') ? '#ff9800' : '#f44336'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              {vitals.map((v, idx) => (
                <Chip
                  key={idx}
                  label={`${v.name}: ${v.rawValue} (${v.status})`}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor:
                      v.status.includes('Normal') ? '#4caf50' :
                      v.status.includes('Elevated') ? '#ff9800' : '#f44336',
                    color:
                      v.status.includes('Normal') ? '#4caf50' :
                      v.status.includes('Elevated') ? '#ff9800' : '#f44336'
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}


      {/* Text Sections */}
      {/* Text Sections */}
      {sections.map((section, idx) => {
        const parsed = parseSection(section);
        if (!parsed) return null;

        const bgColor = getRiskColor(parsed.title);
        const icon = getRiskIcon(parsed.title);

        const colorMap: Record<string, any> = {
          error: { light: '#ffebee', main: '#f44336', pale: '#ffcdd2' },
          warning: { light: '#fff3e0', main: '#ff9800', pale: '#ffe0b2' },
          success: { light: '#e8f5e9', main: '#4caf50', pale: '#c8e6c9' },
          info: { light: '#e3f2fd', main: '#2196f3', pale: '#bbdefb' },
          default: { light: '#f5f5f5', main: '#757575', pale: '#e0e0e0' }
        };

        const colors = colorMap[bgColor] || colorMap.default;

        return (
          <Card
            key={idx}
            variant="outlined"
            sx={{
              borderLeft: `4px solid ${colors.main}`,
              bgcolor: colors.light,
              borderColor: colors.pale,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ fontSize: '20px', mt: 0.5, minWidth: '24px' }}>
                  {icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      color: colors.main,
                      mb: 1.5,
                      textTransform: 'uppercase',
                      fontSize: '0.9rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {parsed.title}
                  </Typography>

                  {parsed.items.length > 0 && (
                    <Stack spacing={0.8}>
                      {parsed.items.map((item, itemIdx) => (
                        <Box key={itemIdx} sx={{ display: 'flex', gap: 1.5 }}>
                          <Box sx={{ color: colors.main, fontWeight: 600, minWidth: '4px' }}>•</Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.primary',
                              lineHeight: 1.6,
                              flex: 1
                            }}
                          >
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

interface TrendPoint {
  date: string;
  value: number | string;
  unit: string;
  status: string;
}

interface LabTrendsProps {
  patientId?: string;
  trends?: Record<string, TrendPoint[]>;
  visits?: any[]; // Array of visits with vitals data
}

export const LabTrends: React.FC<LabTrendsProps> = ({ patientId, trends: initialTrends, visits }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(!initialTrends && !visits);
  const [trends, setTrends] = useState<Record<string, TrendPoint[]>>(initialTrends || {});
  const [error, setError] = useState<string | null>(null);
  const [selectedTests, setSelectedTests] = useState<string[]>(initialTrends ? Object.keys(initialTrends).slice(0, 3) : []);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    // If visits data is provided, try to aggregate vitals from all visits
    if (visits && visits.length > 0) {
      const aggregatedTrends = aggregateVitalsFromVisits(visits);
      // If vitals were found, use them
      if (Object.keys(aggregatedTrends).length > 0) {
        setTrends(aggregatedTrends);
        setSelectedTests(Object.keys(aggregatedTrends).slice(0, 3));
        setLoading(false);
        return;
      }
      // If no vitals found in provided visits, fall through to fetch from API
    }

    if (initialTrends) {
      setTrends(initialTrends);
      setSelectedTests(Object.keys(initialTrends).slice(0, 3));
      setLoading(false);
      return;
    }

    const fetchTrends = async () => {
      if (!patientId) return;
      setLoading(true);
      setError(null);
      try {
        // Fetch visits with vitals data
        const response = await api.visits.getWithVitals(patientId);
        if (response.data && response.data.length > 0) {
          const aggregatedTrends = aggregateVitalsFromVisits(response.data);
          setTrends(aggregatedTrends);
          // Auto-select first 3 tests if available
          const testNames = Object.keys(aggregatedTrends);
          setSelectedTests(testNames.slice(0, 3));
        }
      } catch (err: any) {
        safeConsole.error('Failed to fetch visit trends:', err);
        setError('Failed to load clinical trends.');
      } finally {
        setLoading(false);
      }
    };

    fetchTrends();
  }, [patientId, initialTrends, visits]);

  // Runs independently of the vitals-trend fetch above so the AI insights section fills in
  // whether or not there's enough chart data to plot.
  useEffect(() => {
    if (patientId) fetchAiSummary(patientId);
  }, [patientId]);

  const fetchAiSummary = async (pId: string) => {
    try {
      setAiLoading(true);
      const response = await api.ai.getPatientSummary(pId);
      if (response.data) {
        setAiSummary(response.data);
      }
    } catch (err: any) {
      safeConsole.error('Failed to fetch AI summary:', err);
      // Don't show error to user, AI summary is optional
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>;
  }

  const testNames = Object.keys(trends);

  if (testNames.length === 0) {
    return (
      <Box>
        {/* AI Health Summary when no lab data */}
        {aiSummary && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Zap size={20} color={theme.palette.info.main} />
              <Typography variant="h6" fontWeight={600}>AI-Generated Health Insights</Typography>
            </Box>
            <AiInsightsParsed summary={aiSummary} theme={theme} />
          </Box>
        )}

        {/* Empty state message */}
        {aiLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Generating health insights...</Typography>
          </Box>
        )}

        {!aiSummary && !aiLoading && (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2, borderStyle: 'dashed' }}>
            <Box sx={{ opacity: 0.5, mb: 1 }}>
              <TrendingUp size={48} />
            </Box>
            <Typography variant="body1" color="text.secondary">
              Not enough historical data for trend analysis.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Analyze at least 2 lab reports for this patient to see trends, or check AI insights above.
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  const handleTestChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTests: string[],
  ) => {
    if (newTests.length > 0) {
      setSelectedTests(newTests);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Activity size={20} color={theme.palette.primary.main} />
        <Typography variant="h6" fontWeight={600}>Clinical Parameter Trends</Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          SELECT TESTS TO COMPARE:
        </Typography>
        <ToggleButtonGroup
          value={selectedTests}
          onChange={handleTestChange}
          aria-label="selected tests"
          size="small"
          sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: '1px solid !important', borderRadius: '16px !important', ml: '0 !important' } }}
        >
          {testNames.map((name) => (
            <ToggleButton key={name} value={name} sx={{ px: 2, py: 0.5, textTransform: 'none' }}>
              {name}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {selectedTests.map((testName) => {
          const data = trends[testName];
          return (
            <Grid item xs={12} md={selectedTests.length === 1 ? 12 : 6} key={testName}>
              <ParameterChart testName={testName} data={data} theme={theme} />
            </Grid>
          );
        })}
      </Grid>

      {/* AI Health Summary Section */}
      {aiSummary && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Zap size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" fontWeight={600}>Clinical Health Insights</Typography>
            </Box>
            <AiInsightsParsed summary={aiSummary} theme={theme} />
          </Box>
        </>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <FlaskConical size={20} color={theme.palette.text.secondary} />
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            AI-DRIVEN ANALYSIS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lab trends extracted from historical PDF reports. Clinical insights generated from appointments, prescriptions, vitals, and medical history using Claude 3.5. Data is processed securely with HIPAA compliance.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LabTrends;
